"use client"

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { jwtDecode } from "jwt-decode"
import { FiEye, FiCheckCircle, FiTrendingUp, FiTarget, FiActivity, FiChevronDown, FiX } from "react-icons/fi"
import { MdClose } from "react-icons/md"

// Components
import TopBar from "../../components/TopbarNav"
import TaskReportModal from "../../components/programStaff/TaskReportModal"
import TaskViewModal from "../../components/programStaff/TaskViewModal"
import MasterSideBar from "../../components/MasterSideBar"

// API imports
import { getTaskReportByTaskId } from "../../api/taskReportApi"
import { updateTask } from "../../api/task-api"

// Constants
const TASKS_PER_PAGE = 8
const API_BASE_URL = "https://ims.holdthechild.net/api"

const TASK_STATUS_MAP = {
  in_progress: "Progress",
  on_hold: "On Hold",
  completed: "Completed",
  pending: "Pending",
  planned: "Planned",
  cancelled: "Cancelled",
}

const REPORT_STATUS_MAP = {
  pending: "Under Review",
  approved: "Approved",
  rejected: "Needs Revision",
  submitted: "Submitted",
}

const PRIORITY_LEVELS = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
}

/**
 * HELPER COMPONENTS
 */

const Notification = React.memo(({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className="fixed top-24 right-4 max-w-sm w-full bg-red-600 border border-red-700 text-white rounded-xl shadow-2xl p-4 z-[100] animate-slide-in">
      <div className="flex items-center justify-between">
        <p className="text-xs font-black uppercase tracking-widest">{message}</p>
        <button onClick={onClose} className="text-white hover:opacity-70"><FiX className="w-5 h-5" /></button>
      </div>
    </div>
  )
})

const PriorityBadge = React.memo(({ priority }) => {
  const getPriorityConfig = () => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-800 border-red-200"
      case "high": return "bg-orange-100 text-orange-800 border-orange-200"
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200"
      default: return "bg-green-100 text-green-800 border-green-200"
    }
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${getPriorityConfig()}`}>
      {PRIORITY_LEVELS[priority] || priority}
    </span>
  )
})

const StatusDropdown = React.memo(({ taskId, currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const handleStatusUpdate = async (newStatus) => {
    setIsOpen(false)
    try {
      onStatusChange(taskId, newStatus)
      await updateTask(taskId, { status: newStatus })
    } catch (error) {
      console.error("Failed to update status")
    }
  }

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-[10px] font-bold uppercase text-gray-700 hover:bg-gray-200 transition-all">
        {TASK_STATUS_MAP[currentStatus] || currentStatus} <FiChevronDown />
      </button>
      {isOpen && (
        <div className="absolute left-0 mt-2 w-40 bg-white shadow-xl rounded-xl border border-gray-100 z-50 py-2">
          {Object.entries(TASK_STATUS_MAP).map(([key, value]) => (
            <button key={key} onClick={() => handleStatusUpdate(key)} className="w-full text-left px-4 py-2 text-[10px] font-bold uppercase text-gray-600 hover:bg-orange-50 hover:text-orange-600">
              {value}
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

const StatsOverview = React.memo(({ tasks }) => {
  const stats = useMemo(() => {
    const total = tasks.length
    const completed = tasks.filter((t) => t.status === "completed").length
    const review = tasks.filter((t) => t.reportStatus === "pending").length
    return { total, completed, review }
  }, [tasks])

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><FiTarget size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Total Tasks</p>
            <p className="text-2xl font-black text-gray-800">{stats.total}</p>
          </div>
        </div>
      </div>
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center text-green-600"><FiCheckCircle size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Completed</p>
            <p className="text-2xl font-black text-gray-800">{stats.completed}</p>
          </div>
        </div>
      </div>
      <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600"><FiActivity size={24} /></div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Under Review</p>
            <p className="text-2xl font-black text-gray-800">{stats.review}</p>
          </div>
        </div>
      </div>
    </div>
  )
})

const TaskRow = React.memo(({ task, onViewDetails, onOpenReportModal, onStatusUpdate, onShowNotification }) => (
  <tr className="hover:bg-gray-50/50 transition-colors group">
    <td className="px-6 py-4">
      <p className="text-sm font-bold text-gray-800 truncate max-w-[200px]">{task.title}</p>
      <p className="text-[10px] text-gray-400 uppercase font-bold">{task.activityName}</p>
    </td>
    <td className="px-6 py-4">
      <p className="text-[10px] font-bold text-gray-500">{new Date(task.dueDate).toLocaleDateString()}</p>
    </td>
    <td className="px-6 py-4"><StatusDropdown taskId={task.id} currentStatus={task.status} onStatusChange={onStatusUpdate} /></td>
    <td className="px-6 py-4"><PriorityBadge priority={task.priority} /></td>
    <td className="px-6 py-4 text-right">
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => onViewDetails(task)} className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"><FiEye size={18} /></button>
        {task.hasReport ? (
          <span className="text-[10px] font-black text-green-600 uppercase bg-green-50 px-3 py-1 rounded-full">Reported</span>
        ) : (
          <button 
            onClick={() => task.status === 'completed' ? onOpenReportModal(task) : onShowNotification("Complete task first!")}
            className="px-3 py-1.5 bg-orange-600 text-white text-[10px] font-black uppercase rounded-lg shadow-sm hover:bg-orange-700 transition-all"
          >
            Report
          </button>
        )}
      </div>
    </td>
  </tr>
))

const TaskCard = React.memo(({ task, onViewDetails, onOpenReportModal, onStatusUpdate, onShowNotification }) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <h3 className="text-sm font-bold text-gray-800 leading-tight">{task.title}</h3>
      <PriorityBadge priority={task.priority} />
    </div>
    <div className="flex justify-between items-center mb-4">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{new Date(task.dueDate).toLocaleDateString()}</p>
      <StatusDropdown taskId={task.id} currentStatus={task.status} onStatusChange={onStatusUpdate} />
    </div>
    <div className="flex gap-2">
      <button onClick={() => onViewDetails(task)} className="flex-1 py-2 bg-gray-50 text-gray-600 text-[10px] font-black uppercase rounded-lg">Details</button>
      {!task.hasReport && (
        <button 
          onClick={() => task.status === 'completed' ? onOpenReportModal(task) : onShowNotification("Complete task first!")}
          className="flex-1 py-2 bg-orange-600 text-white text-[10px] font-black uppercase rounded-lg"
        >
          Report
        </button>
      )}
    </div>
  </div>
))

const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex justify-center gap-2 mt-8">
    {Array.from({ length: totalPages }).map((_, i) => (
      <button
        key={i}
        onClick={() => onPageChange(i + 1)}
        className={`w-8 h-8 rounded-lg text-[10px] font-black transition-all ${currentPage === i + 1 ? 'bg-orange-600 text-white' : 'bg-white text-gray-400 hover:bg-gray-100'}`}
      >
        {i + 1}
      </button>
    ))}
  </div>
)

const TaskSkeleton = () => <div className="h-16 bg-gray-100 animate-pulse rounded-xl w-full mb-3"></div>

/**
 * MAIN COMPONENT
 */
const TaskDashboard = () => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [selectedTaskForReport, setSelectedTaskForReport] = useState(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [selectedTaskForView, setSelectedTaskForView] = useState(null)
  const [notification, setNotification] = useState({ isVisible: false, message: "" })

  const token = localStorage.getItem("token")
  const staffId = token ? jwtDecode(token).id : null

  const fetchTasks = useCallback(async () => {
    if (!staffId) return
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/staff-tasks/tasks/${staffId}`)
      const result = await response.json()
      if (result.success) {
        const transformed = await Promise.all(result.data.map(async (task) => {
          let report = null
          try { report = await getTaskReportByTaskId(task._id) } catch (e) {}
          return {
            id: task._id, title: task.title, dueDate: task.dueDate, priority: task.priority,
            status: task.status, activityName: task.activity?.activityTitle || "N/A",
            hasReport: !!report, reportStatus: report?.status || null
          }
        }))
        setTasks(transformed)
      }
    } catch (e) { console.error(e) } finally { setLoading(false) }
  }, [staffId])

  useEffect(() => { fetchTasks() }, [fetchTasks])

  const indexOfLastTask = currentPage * TASKS_PER_PAGE
  const currentTasks = tasks.slice(indexOfLastTask - TASKS_PER_PAGE, indexOfLastTask)
  const totalPages = Math.ceil(tasks.length / TASKS_PER_PAGE)

  const handleStatusUpdate = (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t))
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden font-sans">
      <MasterSideBar 
        visible={sidebarOpen} onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed}
        onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />
        {notification.isVisible && <Notification message={notification.message} onClose={() => setNotification({ isVisible: false, message: "" })} />}

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase mb-8">
              My Tasklist
            </h1>
            
            <StatsOverview tasks={tasks} />

            <div className="mt-10 bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-50 flex items-center justify-between">
                <h3 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div> All Tasks
                </h3>
              </div>

              {loading ? (
                <div className="p-6"><TaskSkeleton /><TaskSkeleton /><TaskSkeleton /></div>
              ) : (
                <>
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50/50">
                        <tr>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Task Detail</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Due Date</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Priority</th>
                          <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {currentTasks.map(task => (
                          <TaskRow 
                            key={task.id} task={task} 
                            onViewDetails={t => { setSelectedTaskForView(t); setIsViewModalOpen(true); }}
                            onOpenReportModal={t => { setSelectedTaskForReport(t); setIsReportModalOpen(true); }}
                            onStatusUpdate={handleStatusUpdate}
                            onShowNotification={m => setNotification({ isVisible: true, message: m })}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="lg:hidden p-4 space-y-4">
                    {currentTasks.map(task => (
                      <TaskCard 
                        key={task.id} task={task}
                        onViewDetails={t => { setSelectedTaskForView(t); setIsViewModalOpen(true); }}
                        onOpenReportModal={t => { setSelectedTaskForReport(t); setIsReportModalOpen(true); }}
                        onStatusUpdate={handleStatusUpdate}
                        onShowNotification={m => setNotification({ isVisible: true, message: m })}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
          </div>
        </main>
      </div>

      <TaskReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} task={selectedTaskForReport} staffId={staffId} onUploadSuccess={() => { setIsReportModalOpen(false); fetchTasks(); }} />
      <TaskViewModal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} task={selectedTaskForView} />
    </div>
  )
}

export default TaskDashboard;